import { useEffect, useState, useMemo } from 'react';
import api, { DANCE_CATEGORIES, CONTEST_TYPES, US_STATES, CA_PROVINCES } from '../lib/api';
import EventCard from '../components/EventCard';
import MapView from '../components/MapView';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MagnifyingGlass, Sliders, X } from '@phosphor-icons/react';
import Seo from '../components/Seo';

export default function Events() {
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grid');
  const [filters, setFilters] = useState({
    q: '',
    state: '',
    country: '',
    event_type: '',
    dance_category: '',
    contest_type: '',
    min_payout: '',
    indoor: false,
    camping: false,
    family_friendly: false,
    sort: 'start_date',
  });

  const fetchEvents = async () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v !== false && v !== null && v !== undefined) params[k] = v;
    });
    try {
      const r = await api.get('/events', { params });
      setEvents(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEvents(); /* eslint-disable-next-line */ }, []);

  const onApply = () => fetchEvents();
  const onReset = () => {
    setFilters({ q:'', state:'', country:'', dance_category:'', contest_type:'', min_payout:'', indoor:false, camping:false, family_friendly:false, sort:'start_date' });
    setTimeout(fetchEvents, 0);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo
        path="/events"
        title="All Pow Wows"
        description="Browse every upcoming Native American & First Nations pow wow. Filter by state, tribe, dance category, prize payout, and more. Updated continuously from powwows.com."
        keywords="pow wow calendar, upcoming powwows, native american events, indigenous gatherings, powwow schedule"
      />
      <div className="mb-8">
        <p className="label text-terracotta mb-2">Browse</p>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">All Pow Wows</h1>
        <p className="text-muted-foreground mt-2">{events.length} events · filter by region, payout, or dance style</p>
      </div>

      <div className="grid lg:grid-cols-[300px_1fr] gap-8">
        {/* Filters */}
        <aside className="bg-card border border-border rounded-2xl p-6 h-fit lg:sticky lg:top-24" data-testid="filter-panel">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-xl flex items-center gap-2"><Sliders size={18} weight="duotone" /> Filters</h3>
            <button onClick={onReset} className="text-xs text-muted-foreground hover:text-terracotta" data-testid="filter-reset">Reset</button>
          </div>
          <div className="space-y-5">
            <div>
              <label className="label text-muted-foreground mb-2 block">Search</label>
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={filters.q}
                  onChange={(e) => setFilters({...filters, q: e.target.value})}
                  placeholder="name, tribe, city"
                  className="pl-9 rounded-full"
                  data-testid="filter-search"
                />
              </div>
            </div>
            <div>
              <label className="label text-muted-foreground mb-2 block">Event type</label>
              <Select value={filters.event_type} onValueChange={(v) => setFilters({...filters, event_type: v === 'any' ? '' : v})}>
                <SelectTrigger data-testid="filter-event-type"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">All event types</SelectItem>
                  {eventTypes.map(t => <SelectItem key={t.type} value={t.type}>{t.type} ({t.count})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label text-muted-foreground mb-2 block">Country</label>
              <Select value={filters.country} onValueChange={(v) => setFilters({...filters, country: v === 'any' ? '' : v})}>
                <SelectTrigger data-testid="filter-country"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="USA">United States</SelectItem>
                  <SelectItem value="Canada">Canada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label text-muted-foreground mb-2 block">State / Province</label>
              <Select value={filters.state} onValueChange={(v) => setFilters({...filters, state: v === 'any' ? '' : v})}>
                <SelectTrigger data-testid="filter-state"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="any">Any</SelectItem>
                  {[...US_STATES, ...CA_PROVINCES].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label text-muted-foreground mb-2 block">Dance category</label>
              <Select value={filters.dance_category} onValueChange={(v) => setFilters({...filters, dance_category: v === 'any' ? '' : v})}>
                <SelectTrigger data-testid="filter-dance"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent className="max-h-64">
                  <SelectItem value="any">Any</SelectItem>
                  {DANCE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label text-muted-foreground mb-2 block">Contest type</label>
              <Select value={filters.contest_type} onValueChange={(v) => setFilters({...filters, contest_type: v === 'any' ? '' : v})}>
                <SelectTrigger data-testid="filter-contest"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  {CONTEST_TYPES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label text-muted-foreground mb-2 block">Min. prize pool ($)</label>
              <Input type="number" value={filters.min_payout} onChange={e => setFilters({...filters, min_payout: e.target.value})} placeholder="50000" className="rounded-full" data-testid="filter-payout" />
            </div>
            <div className="space-y-3 pt-2">
              <label className="flex items-center gap-3 text-sm cursor-pointer">
                <Checkbox checked={filters.indoor} onCheckedChange={(v) => setFilters({...filters, indoor: !!v})} data-testid="filter-indoor" /> Indoor venue
              </label>
              <label className="flex items-center gap-3 text-sm cursor-pointer">
                <Checkbox checked={filters.camping} onCheckedChange={(v) => setFilters({...filters, camping: !!v})} data-testid="filter-camping" /> Camping available
              </label>
              <label className="flex items-center gap-3 text-sm cursor-pointer">
                <Checkbox checked={filters.family_friendly} onCheckedChange={(v) => setFilters({...filters, family_friendly: !!v})} data-testid="filter-family" /> Family friendly
              </label>
            </div>
            <div>
              <label className="label text-muted-foreground mb-2 block">Sort</label>
              <Select value={filters.sort} onValueChange={(v) => setFilters({...filters, sort: v})}>
                <SelectTrigger data-testid="filter-sort"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="start_date">Date (soonest)</SelectItem>
                  <SelectItem value="payout">Highest payout</SelectItem>
                  <SelectItem value="created">Recently added</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={onApply} className="w-full bg-forest hover:bg-forest/90 text-bone rounded-full" data-testid="filter-apply">
              Apply filters
            </Button>
          </div>
        </aside>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-5">
            <div className="inline-flex bg-muted rounded-full p-1">
              <button onClick={() => setView('grid')} className={`px-4 py-1.5 text-sm rounded-full ${view==='grid'?'bg-card shadow':''}`} data-testid="view-grid">Grid</button>
              <button onClick={() => setView('map')} className={`px-4 py-1.5 text-sm rounded-full ${view==='map'?'bg-card shadow':''}`} data-testid="view-map">Map</button>
            </div>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({length:6}).map((_,i)=><div key={i} className="aspect-[16/12] bg-muted animate-pulse rounded-2xl" />)}
            </div>
          ) : view === 'grid' ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map(e => <EventCard key={e.event_id} event={e} />)}
            </div>
          ) : (
            <div className="bg-card border border-border rounded-2xl p-3">
              <MapView events={events} height="75vh" zoom={4} />
            </div>
          )}

          {!loading && events.length === 0 && (
            <div className="text-center py-20 text-muted-foreground">
              <p>No events match your filters. Try resetting.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
